/**
 * Victory Screen Component - "The Payday"
 * Premium casino-style game over screen with chest animation
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/button';
import { Trophy, Skull, Coins, RefreshCw, LogOut, Sparkles } from 'lucide-react';
import audioManager from '../lib/AudioManager';

const VictoryScreen = ({
  isOpen,
  isWinner,
  payout,
  winnerUsername,
  playerScore,
  onPlayAgain,
  onExit,
  equippedCardName
}) => {
  const [phase, setPhase] = useState('chest'); // 'chest' | 'opening' | 'reveal'
  const [coinsAnimating, setCoinsAnimating] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setPhase('chest');
      
      // Play appropriate sound
      if (isWinner) {
        audioManager.play('chestOpen');
      } else {
        audioManager.play('gameOver');
      }
      
      // Animation sequence
      const timer1 = setTimeout(() => {
        setPhase('opening');
        if (isWinner) {
          audioManager.play('jackpot');
        }
      }, 800);
      
      const timer2 = setTimeout(() => {
        setPhase('reveal');
        if (isWinner) {
          setCoinsAnimating(true);
          audioManager.play('coinRain');
          audioManager.play('victory');
        }
      }, 1800);

      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
      };
    }
  }, [isOpen, isWinner]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[100] flex items-center justify-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        {/* Dark overlay with radial glow */}
        <div 
          className="absolute inset-0 bg-black/95"
          style={{
            background: isWinner 
              ? 'radial-gradient(ellipse at center, rgba(255,215,0,0.15) 0%, rgba(0,0,0,0.95) 70%)'
              : 'radial-gradient(ellipse at center, rgba(255,0,0,0.1) 0%, rgba(0,0,0,0.95) 70%)'
          }}
        />

        {/* Coin rain effect for winners */}
        {coinsAnimating && isWinner && (
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            {[...Array(30)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute text-3xl"
                initial={{ 
                  x: Math.random() * window.innerWidth, 
                  y: -50,
                  rotate: 0,
                  opacity: 1
                }}
                animate={{ 
                  y: window.innerHeight + 50,
                  rotate: 360 * (Math.random() > 0.5 ? 1 : -1),
                  opacity: [1, 1, 0]
                }}
                transition={{ 
                  duration: 2 + Math.random() * 2,
                  delay: Math.random() * 1.5,
                  ease: 'linear'
                }}
              >
                {Math.random() > 0.3 ? '🪙' : '💰'}
              </motion.div>
            ))}
          </div>
        )}

        {/* Main content card */}
        <motion.div
          className="relative z-10 w-full max-w-md mx-4"
          initial={{ scale: 0.8, y: 50 }}
          animate={{ scale: 1, y: 0 }}
          transition={{ type: 'spring', damping: 15 }}
        >
          {/* Glowing border */}
          <div 
            className={`absolute -inset-1 rounded-3xl blur-xl ${
              isWinner ? 'bg-gradient-to-r from-yellow-500 via-orange-500 to-yellow-500' : 'bg-gradient-to-r from-red-800 via-red-600 to-red-800'
            } opacity-50`}
          />
          
          <div className={`relative bg-[#0a0a12] border-4 rounded-2xl overflow-hidden ${
            isWinner ? 'border-yellow-500' : 'border-red-600'
          }`}>
            
            {/* Header stripe */}
            <div className={`h-2 ${
              isWinner 
                ? 'bg-gradient-to-r from-yellow-500 via-orange-400 to-yellow-500' 
                : 'bg-gradient-to-r from-red-700 via-red-500 to-red-700'
            }`} />

            <div className="p-6 text-center">
              
              {/* PHASE 1: Chest Animation */}
              {phase === 'chest' && (
                <motion.div
                  className="py-8"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', damping: 10 }}
                >
                  <motion.div
                    className="text-[100px] leading-none"
                    animate={{ 
                      y: [0, -10, 0],
                      rotateZ: [-2, 2, -2]
                    }}
                    transition={{ duration: 0.5, repeat: Infinity }}
                  >
                    {isWinner ? '📦' : '💀'}
                  </motion.div>
                  <p className="mt-4 text-white/60 font-space uppercase tracking-widest">
                    {isWinner ? 'Processing Payout...' : 'Game Over...'}
                  </p>
                </motion.div>
              )}

              {/* PHASE 2: Opening Animation */}
              {phase === 'opening' && (
                <motion.div
                  className="py-8"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  <motion.div
                    className="text-[100px] leading-none"
                    animate={{ 
                      scale: [1, 1.3, 1.1],
                      rotateY: [0, 180, 360]
                    }}
                    transition={{ duration: 1 }}
                  >
                    {isWinner ? '🎁' : '💔'}
                  </motion.div>
                  <motion.div
                    className="mt-4 flex justify-center gap-2"
                    animate={{ opacity: [0.5, 1, 0.5] }}
                    transition={{ duration: 0.5, repeat: Infinity }}
                  >
                    <Sparkles className="w-5 h-5 text-yellow-400" />
                    <span className="text-yellow-400 font-space uppercase">
                      {isWinner ? 'Opening...' : 'Calculating...'}
                    </span>
                    <Sparkles className="w-5 h-5 text-yellow-400" />
                  </motion.div>
                </motion.div>
              )}

              {/* PHASE 3: Reveal */}
              {phase === 'reveal' && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ type: 'spring', damping: 12 }}
                >
                  {isWinner ? (
                    <>
                      {/* Winner Trophy */}
                      <motion.div
                        className="relative inline-block"
                        animate={{ 
                          rotateZ: [-5, 5, -5],
                          y: [0, -8, 0]
                        }}
                        transition={{ duration: 2, repeat: Infinity }}
                      >
                        <motion.div
                          className="text-[80px] leading-none filter drop-shadow-lg"
                          style={{ filter: 'drop-shadow(0 0 30px rgba(255,215,0,0.5))' }}
                        >
                          🏆
                        </motion.div>
                        {/* Sparkle effects */}
                        {[...Array(6)].map((_, i) => (
                          <motion.div
                            key={i}
                            className="absolute w-2 h-2 bg-yellow-300 rounded-full"
                            style={{
                              top: '50%',
                              left: '50%'
                            }}
                            animate={{
                              x: [0, (Math.random() - 0.5) * 100],
                              y: [0, (Math.random() - 0.5) * 100],
                              opacity: [1, 0],
                              scale: [1, 0]
                            }}
                            transition={{
                              duration: 1,
                              repeat: Infinity,
                              delay: i * 0.2
                            }}
                          />
                        ))}
                      </motion.div>

                      {/* Winner Title */}
                      <motion.h2
                        className="font-anton text-5xl mt-4"
                        style={{
                          background: 'linear-gradient(180deg, #FFD700 0%, #FF8C00 50%, #FFD700 100%)',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))'
                        }}
                        animate={{ scale: [1, 1.05, 1] }}
                        transition={{ duration: 0.5, repeat: Infinity }}
                      >
                        WINNER!
                      </motion.h2>

                      {/* Payout Amount */}
                      <div className="mt-6 p-4 bg-gradient-to-br from-yellow-500/20 to-orange-500/10 rounded-xl border-2 border-yellow-500/50">
                        <p className="text-sm text-yellow-300/70 font-space uppercase tracking-wider mb-2">
                          PAYOUT PROCESSED
                        </p>
                        <motion.div
                          className="flex items-center justify-center gap-3"
                          animate={{ scale: [1, 1.02, 1] }}
                          transition={{ duration: 0.8, repeat: Infinity }}
                        >
                          <Coins className="w-8 h-8 text-yellow-400" />
                          <span 
                            className="font-anton text-5xl"
                            style={{
                              background: 'linear-gradient(180deg, #00FF88 0%, #00CC66 100%)',
                              WebkitBackgroundClip: 'text',
                              WebkitTextFillColor: 'transparent'
                            }}
                          >
                            +{payout?.toLocaleString()}
                          </span>
                        </motion.div>
                        <p className="text-lg text-yellow-400 font-anton mt-2">$SLAP</p>
                      </div>

                      {/* Your Score */}
                      <div className="mt-4 text-center">
                        <span className="text-white/50 text-sm">Your Score: </span>
                        <span className="text-cyan-400 font-anton text-xl">{playerScore}</span>
                      </div>
                    </>
                  ) : (
                    <>
                      {/* Loser Skull */}
                      <motion.div
                        className="text-[80px] leading-none"
                        animate={{ rotateZ: [-3, 3, -3] }}
                        transition={{ duration: 1, repeat: Infinity }}
                        style={{ filter: 'drop-shadow(0 0 20px rgba(255,0,0,0.4))' }}
                      >
                        💀
                      </motion.div>

                      {/* Loser Title */}
                      <h2 
                        className="font-anton text-5xl mt-4 text-red-500"
                        style={{ textShadow: '0 0 20px rgba(255,0,0,0.5)' }}
                      >
                        REKT!
                      </h2>

                      {/* Winner Info */}
                      <div className="mt-6 p-4 bg-red-500/10 rounded-xl border border-red-500/30">
                        <p className="text-white/60 text-sm mb-2">
                          {winnerUsername} won the pot
                        </p>
                        <div className="flex items-center justify-center gap-2">
                          <Trophy className="w-5 h-5 text-yellow-400" />
                          <span className="font-anton text-2xl text-yellow-400">
                            {payout?.toLocaleString()} $SLAP
                          </span>
                        </div>
                      </div>

                      {/* Your Score */}
                      <div className="mt-4 text-center">
                        <span className="text-white/50 text-sm">Your Score: </span>
                        <span className="text-red-400 font-anton text-xl">{playerScore}</span>
                      </div>
                    </>
                  )}

                  {/* Equipped Card Info */}
                  {equippedCardName && (
                    <div className="mt-4 text-center text-sm text-white/40">
                      Battled with: <span className="text-cyan-400">{equippedCardName}</span>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex gap-4 mt-8">
                    <Button
                      onClick={onPlayAgain}
                      className={`flex-1 h-14 font-anton text-xl rounded-xl ${
                        isWinner
                          ? 'bg-gradient-to-r from-yellow-500 via-orange-500 to-yellow-500 text-black hover:brightness-110'
                          : 'bg-gradient-to-r from-red-600 to-orange-600 text-white hover:brightness-110'
                      }`}
                      data-testid="play-again-button"
                    >
                      <RefreshCw className="w-5 h-5 mr-2" />
                      PLAY AGAIN
                    </Button>
                    <Button
                      onClick={onExit}
                      variant="outline"
                      className="flex-1 h-14 font-anton text-xl rounded-xl border-2 border-white/20 text-white hover:bg-white/10"
                      data-testid="exit-button"
                    >
                      <LogOut className="w-5 h-5 mr-2" />
                      EXIT
                    </Button>
                  </div>
                </motion.div>
              )}
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default VictoryScreen;
